import React, { useState, useEffect } from 'react';
import { Plus, FileText, Trash2, Download } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { documents as api, uploads, type NexoraDocument, type ApiError } from '../../services/api';
import DataGrid from '../../components/DataGrid';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};

const SCOPES = [
  { value: 'SOCIETY', label: 'Society-wide (all residents)' },
  { value: 'PERSONAL', label: 'Personal (one unit only)' },
  { value: 'MANAGEMENT', label: 'Management only (admins/treasurer)' },
];

const scopeColor = (scope: string) =>
  scope === 'MANAGEMENT' ? { bg: 'rgba(109,40,217,0.1)', text: '#6D28D9' }
    : scope === 'PERSONAL' ? { bg: 'rgba(245,158,11,0.1)', text: '#D97706' }
    : { bg: 'rgba(16,185,129,0.1)', text: '#10B981' };

const INIT = { title: '', category: '', visibilityScope: 'SOCIETY', unitId: '' };

const DocumentsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const isAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'SOCIETY_ADMIN' || user?.role === 'STAFF';

  const [items, setItems] = useState<NexoraDocument[]>([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(INIT);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<NexoraDocument | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openError, setOpenError] = useState('');

  const refresh = () => {
    if (!societyId) { setLoading(false); return; }
    setLoading(true);
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(refresh, [societyId]);

  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !file) { setFormError('Please choose a file to upload.'); return; }
    setSaving(true); setFormError('');
    try {
      const uploaded = await uploads.upload(societyId, file);
      const created = await api.create(societyId, {
        title: form.title,
        category: form.category || undefined,
        visibilityScope: form.visibilityScope,
        unitId: form.visibilityScope === 'PERSONAL' ? (form.unitId || undefined) : undefined,
        fileUrl: uploaded.url,
      });
      setItems(p => [created, ...p]);
      setShowForm(false); setForm(INIT); setFile(null);
    } catch (err) {
      setFormError((err as ApiError).message || 'Failed to upload document');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!societyId || !deleteTarget) return;
    setDeleting(true); setDeleteError('');
    try {
      await api.delete(societyId, deleteTarget.id);
      setItems(p => p.filter(d => d.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError((err as ApiError).message || 'Failed to delete document');
    } finally { setDeleting(false); }
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });

  const handleOpen = async (d: NexoraDocument) => {
    if (!societyId) return;
    setOpeningId(d.id); setOpenError('');
    try {
      const blob = await uploads.download(societyId, d.fileUrl);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (err) {
      setOpenError((err as ApiError).message || 'Failed to open document');
    } finally { setOpeningId(null); }
  };

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Documents
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>{items.length} documents visible to you</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowForm(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> Upload Document
          </button>
        )}
      </div>

      {(deleteError || openError) && (
        <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, fontSize: 12, color: '#EF4444', marginBottom: 16 }}>
          {deleteError || openError}
        </div>
      )}

      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            {
              key: 'title', header: 'Title',
              render: (d: NexoraDocument) => (
                <button
                  onClick={() => handleOpen(d)}
                  disabled={openingId === d.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--nx-text-primary)', background: 'none', border: 'none', padding: 0, cursor: openingId === d.id ? 'wait' : 'pointer', font: 'inherit' }}
                >
                  <FileText size={14} style={{ flexShrink: 0 }} /> {d.title}
                </button>
              ),
            },
            {
              key: 'category', header: 'Category',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (d: NexoraDocument) => d.category || '—',
            },
            {
              key: 'visibilityScope', header: 'Visibility',
              render: (d: NexoraDocument) => {
                const sc = scopeColor(d.visibilityScope);
                return (
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                    {d.visibilityScope}
                  </span>
                );
              },
            },
            {
              key: 'createdAt', header: 'Uploaded',
              cellStyle: { color: 'var(--nx-text-muted)' },
              render: (d: NexoraDocument) => d.createdAt ? fmt(d.createdAt) : '—',
            },
            {
              key: 'actions', header: '',
              cellStyle: { textAlign: 'right', whiteSpace: 'nowrap' },
              render: (d: NexoraDocument) => (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => handleOpen(d)}
                    disabled={openingId === d.id}
                    className="nx-btn nx-btn-outline nx-btn-sm"
                    style={{ gap: 4, cursor: openingId === d.id ? 'wait' : 'pointer' }}
                  >
                    <Download size={12} /> {openingId === d.id ? 'Opening…' : 'Open'}
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => setDeleteTarget(d)}
                      className="nx-btn nx-btn-outline nx-btn-sm"
                      style={{ color: '#EF4444', borderColor: 'rgba(239,68,68,0.3)' }}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={d => d.id}
          loading={loading}
          emptyIcon={<FileText size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No documents available yet."
        />
      </div>

      {showForm && (
        <Modal
          title="Upload Document" submitLabel="Upload" error={formError} isLoading={saving}
          onClose={() => { setShowForm(false); setForm(INIT); setFile(null); setFormError(''); }}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Title *</label>
            <input name="title" value={form.title} onChange={change} placeholder="Society bylaws, AGM minutes…" style={inp} required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Category</label>
              <input name="category" value={form.category} onChange={change} placeholder="Legal, Financial…" style={inp} />
            </div>
            <div>
              <label style={lbl}>Visibility *</label>
              <select name="visibilityScope" value={form.visibilityScope} onChange={change} style={inp}>
                {SCOPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          {form.visibilityScope === 'PERSONAL' && (
            <div>
              <label style={lbl}>Unit ID *</label>
              <input name="unitId" value={form.unitId} onChange={change} placeholder="Unit this document belongs to" style={inp} required />
            </div>
          )}
          <div>
            <label style={lbl}>File *</label>
            <input
              type="file" accept="image/*,video/*,application/pdf"
              onChange={e => setFile(e.target.files?.[0] ?? null)}
              style={inp}
            />
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        variant="danger"
        title="Delete document?"
        message={`This will permanently remove "${deleteTarget?.title}". This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        isLoading={deleting}
        error={deleteError}
        onConfirm={handleDelete}
        onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
      />
    </PageLayout>
  );
};

export default DocumentsPage;
